<script setup lang="ts">
import { computed, ref } from 'vue'
import { useAppToast } from '@shared/composables/useAppToast'
import { useMarketsQuery } from '@modules/masters'
import type { SelectOption } from '@shared/types'
import type { FeeLifecycleStage, FeePartyRole } from '@shared/constants/fees'
import FeeConfig, { type FeeConfigViewMode } from '../components/FeeConfig.vue'
import { useFeesMasterQuery } from '../api/queries/useFeesMasterQuery'
import { useFeeDefinitionVersionsQuery } from '../api/queries/useFeeDefinitionVersionsQuery'
import { useCreateFeeDefinitionMutation } from '../api/mutations/useCreateFeeDefinitionMutation'
import { useSupersedeFeeDefinitionMutation } from '../api/mutations/useSupersedeFeeDefinitionMutation'
import { useDisableFeeDefinitionMutation } from '../api/mutations/useDisableFeeDefinitionMutation'
import {
  toCreateFeePayload,
  toSupersedeFeePayload,
  type FeeCodeInForce,
} from '../utils/feeFormUtils'
import type { FeeDefinition, FeeDefinitionFormValues } from '../types/feeTypes'
import type { GetFeesMasterParams } from '../api/feesMasterContracts'

const ALL_STAGES = 'all'
const ALL_ROLES = 'all'
const STATUS_ALL = 'all'
const STATUS_ACTIVE = 'active'

const { addToast } = useAppToast()

const viewMode = ref<FeeConfigViewMode>('list')
const selectedFee = ref<FeeDefinition | null>(null)
const isHistoryModalOpen = ref(false)
const stageFilter = ref<string>(ALL_STAGES)
const statusFilter = ref<string>(STATUS_ACTIVE)
const paidByFilter = ref<string>(ALL_ROLES)
const paidToFilter = ref<string>(ALL_ROLES)

// Stage and status narrow the query server-side; the two party roles filter the
// result client-side because the API carries no role parameter.
const queryParams = computed<GetFeesMasterParams>(() => ({
  ...(stageFilter.value !== ALL_STAGES && {
    lifecycleStage: stageFilter.value as FeeLifecycleStage,
  }),
  ...(statusFilter.value !== STATUS_ALL && { isActive: statusFilter.value === STATUS_ACTIVE }),
}))

const feesQuery = useFeesMasterQuery(queryParams)
const versionsQuery = useFeeDefinitionVersionsQuery(
  () => selectedFee.value?.id ?? null,
  () => isHistoryModalOpen.value,
)

// The canonical market list, owned by masters/markets. A definition is scoped
// to one market or to every market (null), so the form picks from this rather
// than from a list of its own.
const marketsQuery = useMarketsQuery()
const marketOptions = computed<SelectOption[]>(() =>
  (marketsQuery.data.value ?? []).map((market) => ({ label: market.name, value: market.id })),
)

// A second, unfiltered read of the same endpoint, fetched only while the create
// form is open. The list above it is narrowed by the stage and status filters,
// and a code is taken by any version still in force — a disabled one included —
// so the filtered rows would let a clash through.
const isCreating = computed(() => viewMode.value === 'create')
const codesInForceQuery = useFeesMasterQuery({}, isCreating)
const codesInForce = computed<FeeCodeInForce[]>(() =>
  (codesInForceQuery.data.value?.rows ?? []).map((fee) => ({
    code: fee.code,
    marketId: fee.marketId,
  })),
)

const createMutation = useCreateFeeDefinitionMutation()
const supersedeMutation = useSupersedeFeeDefinitionMutation()
const disableMutation = useDisableFeeDefinitionMutation()

const visibleFees = computed<FeeDefinition[]>(() => {
  const rows = feesQuery.data.value?.rows ?? []
  return rows.filter(
    (fee) =>
      matchesRole(fee.paidByRole, paidByFilter.value) &&
      matchesRole(fee.paidToRole, paidToFilter.value),
  )
})

function matchesRole(role: FeePartyRole, filter: string): boolean {
  return filter === ALL_ROLES || role === filter
}

const isSaving = computed(() => createMutation.isPending.value || supersedeMutation.isPending.value)

function handleStageFilterChange(stage: string): void {
  stageFilter.value = stage
}

function handleStatusFilterChange(status: string): void {
  statusFilter.value = status
}

function handlePaidByFilterChange(role: string): void {
  paidByFilter.value = role
}

function handlePaidToFilterChange(role: string): void {
  paidToFilter.value = role
}

function openDetails(fee: FeeDefinition): void {
  selectedFee.value = fee
  viewMode.value = 'details'
}

function openEdit(fee: FeeDefinition): void {
  selectedFee.value = fee
  viewMode.value = 'edit'
}

function openCreate(): void {
  selectedFee.value = null
  viewMode.value = 'create'
}

function openHistory(fee: FeeDefinition): void {
  selectedFee.value = fee
  isHistoryModalOpen.value = true
}

function backToList(): void {
  viewMode.value = 'list'
  selectedFee.value = null
}

function closeHistory(): void {
  isHistoryModalOpen.value = false
}

function handleSave(values: FeeDefinitionFormValues): void {
  if (viewMode.value === 'create') {
    createFee(values)
    return
  }
  const fee = selectedFee.value
  if (fee) supersedeFee(fee, values)
}

function createFee(values: FeeDefinitionFormValues): void {
  createMutation.mutate(toCreateFeePayload(values), {
    onSuccess: (created) => {
      addToast({
        type: 'success',
        title: 'Fee created',
        message: `"${created.name}" is now defined.`,
      })
      backToList()
    },
    onError: () => notifySaveFailed(),
  })
}

function supersedeFee(fee: FeeDefinition, values: FeeDefinitionFormValues): void {
  supersedeMutation.mutate(
    { id: fee.id, payload: toSupersedeFeePayload(values) },
    {
      onSuccess: (superseding) => {
        selectedFee.value = superseding
        addToast({
          type: 'success',
          title: 'New version saved',
          message: `"${superseding.name}" now applies from ${superseding.validFrom.slice(0, 10)}.`,
        })
        viewMode.value = 'details'
      },
      onError: () => notifySaveFailed(),
    },
  )
}

function handleDisable(fee: FeeDefinition): void {
  disableMutation.mutate(fee.id, {
    onSuccess: () => {
      addToast({
        type: 'success',
        title: 'Fee disabled',
        message: `"${fee.name}" will not be applied to new properties.`,
      })
      backToList()
    },
    onError: () =>
      addToast({
        type: 'error',
        title: 'Could not disable the fee',
        message: 'The change was not saved. Try again.',
      }),
  })
}

function notifySaveFailed(): void {
  addToast({
    type: 'error',
    title: 'Could not save the fee',
    message: 'The change was not saved. Check the values and try again.',
  })
}
</script>

<template>
  <FeeConfig
    :view-mode="viewMode"
    :fees="visibleFees"
    :selected-fee="selectedFee"
    :stage-filter="stageFilter"
    :status-filter="statusFilter"
    :paid-by-filter="paidByFilter"
    :paid-to-filter="paidToFilter"
    :markets="marketOptions"
    :codes-in-force="codesInForce"
    :is-loading="feesQuery.isLoading.value"
    :is-saving="isSaving"
    :is-disabling="disableMutation.isPending.value"
    :is-history-modal-open="isHistoryModalOpen"
    :history-versions="versionsQuery.data.value ?? []"
    :is-history-loading="versionsQuery.isLoading.value"
    @stage-filter-change="handleStageFilterChange"
    @status-filter-change="handleStatusFilterChange"
    @paid-by-filter-change="handlePaidByFilterChange"
    @paid-to-filter-change="handlePaidToFilterChange"
    @view-details="openDetails"
    @view-history="openHistory"
    @edit-fee="openEdit"
    @create-fee="openCreate"
    @disable-fee="handleDisable"
    @save="handleSave"
    @back-to-list="backToList"
    @close-history="closeHistory"
  />
</template>
